import {DEMO_REQUEST_PATH} from '@/components/www/demo-request/demoPage'
import {LISTING_REJECTIONS_LP, type LeadInPoint} from '@/components/www/listing-rejections/listingRejectionsLp'
import type {LandingFaqItem} from '@/components/www/landing/landingFaq'
import type {LandingComparison} from '@/components/www/landing/landingComparison'
import type {NordstromAboutPillar, NordstromHowStep} from '@/components/www/sell-on-nordstrom/nordstromLp'
import type {PointVisual} from '@/components/www/listing-rejections/pointVisuals'

export const MARKETPLACE_LISTING_ALTERNATIVES_PATH = '/fashion-marketplace-listing-alternatives'

export function alternativesDemoPath(cta: string): string {
    return `${DEMO_REQUEST_PATH}?source=marketplace-listing-alternatives&cta=${encodeURIComponent(cta)}`
}

export const ALTERNATIVES_LP = {
    meta: {
        title: 'Marketplace listing for fashion brands already selling',
        description:
            'Already live on Nordstrom, Macy\'s, or Target+? Osello reads Shopify, merges colorways, and fixes rejections — without a feed or a spreadsheet. Book a 20-min demo. From $499/mo.',
    },
    hero: {
        lines: [
            'Your fashion catalog.',
            'On every marketplace.',
            'Listed in minutes.',
        ] as const,
        sub:
            'Already live on Nordstrom, Macy\'s, or Target+? Osello reads Shopify, merges colorways, crops images to each marketplace, and resubmits rejections. No more spreadsheets to manage.',
        cta: 'Book a 20-min demo',
        ctaNote: 'See the listing workflow, review your current setup,',
        ctaSupport: 'and discuss what switching would involve in 20min demo.',
    },
    about: {
        title: 'So, who is Osello?',
        lede:
            'Osello is the marketplace integration platform built for fashion brands. One Shopify connection. Agents write the listings, merge colorways, crop images to each retailer, and resubmit rejections. You keep the accounts you already have.',
        pillars: [
            {
                id: 'platform',
                title: 'Software does the heavy lifting',
                body:
                    'Products, imagery, inventory, and orders sync between Shopify and the marketplaces you already sell on. The listing work leaves the spreadsheet.',
            },
            {
                id: 'fashion',
                title: 'Fashion is the specialty',
                body:
                    'Size runs, colorways, seasonal drops, imagery standards. Universal Standard, Noli, and Furniq UK run catalogs through it.',
            },
            {
                id: 'growth',
                title: 'Already live, then everywhere',
                body:
                    'Keep Nordstrom, Macy\'s, Target+. Add the next retailer from the same catalog. You are not reapplying.',
            },
        ] satisfies NordstromAboutPillar[],
        cta: 'Book a 20-min demo',
    },
    stories: {
        eyebrow: 'Customer success stories',
        title: 'Hear what changed for fashion brands using Osello.',
        sub: 'No more manual catalog uploads. Fewer listing rejections. More time for the work that moves their brands forward.',
        /** Universal Standard, Noli, Furniq — Group B first, spreadsheet voice second. */
        storyOrder: ['01', '03', '02'] as const,
    },
    ctaBand: {
        note: '20 minutes. Your Shopify fashion catalog.',
        cta: 'Book a 20-min demo',
    },
    howItWorks: {
        title: 'Three steps. You stay live.',
        steps: [
            {
                id: 'connect',
                step: '01',
                title: 'Connect Shopify',
                body: 'One click. Catalog, variants, images, inventory. No export, no spreadsheet.',
            },
            {
                id: 'build',
                step: '02',
                title: 'We build the listings',
                body: 'Agents write the fields, merge colorways, and crop images to each retailer. You approve before anything submits.',
            },
            {
                id: 'live',
                step: '03',
                title: 'Cut over when it is ready',
                body: 'Your live listings stay up. We switch the connection after you have seen the mapping work. Then add the next marketplace from the same catalog.',
            },
        ] satisfies NordstromHowStep[],
        cta: 'Book a 20-min demo',
    },
    continuity: {
        title: 'Nothing goes dark while you move.',
        intro: 'The question everyone asks first, so here it is before you have to.',
        points: [
            {
                id: 'live',
                lead: 'Your live listings stay live.',
                body: 'We build alongside your current setup and cut over once the mapping is verified, not before.',
            },
            {
                id: 'account',
                lead: 'Your marketplace accounts stay yours.',
                body: 'You are not reapplying. Nordstrom, Macy\'s, Target+ — the seller accounts do not reset. This is a connection change.',
            },
            {
                id: 'orders',
                lead: 'In-flight orders keep flowing.',
                body: 'Orders land in Shopify the way they already do, throughout.',
            },
        ] satisfies LeadInPoint[],
    },
    continuityVisuals: [
        {
            id: 'live',
            crumb: 'Cutover · Status',
            status: 'No downtime',
            statusTone: 'good',
            body: {
                kind: 'rows',
                rows: [
                    {label: 'Your current provider', chip: 'Live', tone: 'good'},
                    {label: 'Live marketplace listings', chip: 'Live', tone: 'good'},
                    {label: 'Osello mapping', chip: 'Building', tone: 'neutral'},
                    {label: 'Cutover', chip: 'Not yet', tone: 'neutral'},
                ],
            },
        },
        {
            id: 'account',
            crumb: 'Seller accounts',
            status: 'Unchanged',
            statusTone: 'good',
            body: {
                kind: 'rows',
                rows: [
                    {label: 'Seller accounts', chip: 'Unchanged', tone: 'good'},
                    {label: 'Approval standing', chip: 'Unchanged', tone: 'good'},
                    {label: 'Reapplication needed', chip: 'No', tone: 'good'},
                    {label: 'Connected through', chip: 'Osello', tone: 'neutral'},
                ],
            },
        },
        {
            id: 'orders',
            crumb: 'Orders · Marketplace to Shopify',
            status: 'Flowing',
            statusTone: 'good',
            body: {
                kind: 'rows',
                rows: [
                    {label: 'Order #40118', chip: 'In Shopify', tone: 'good'},
                    {label: 'Order #40119', chip: 'In Shopify', tone: 'good'},
                    {label: 'Order #40120', chip: 'In Shopify', tone: 'good'},
                    {label: 'Order #40121', chip: 'In Shopify', tone: 'good'},
                ],
            },
        },
    ] satisfies PointVisual[],
    comparison: LISTING_REJECTIONS_LP.comparison as LandingComparison,
    faq: {
        title: 'Questions, answered.',
        items: [
            {
                id: 'leave-provider',
                question: 'Do we have to turn off our current tool first?',
                answer:
                    'No. We build alongside what you have now. Nothing gets canceled until the new mapping is verified and you have seen it working.',
            },
            {
                id: 'stay-live',
                question: 'We\'re already live. Will listings go down?',
                answer:
                    'No. Your live listings stay live. Your marketplace accounts stay yours. You are not reapplying. In-flight orders keep landing in Shopify.',
            },
            {
                id: 'already-listed',
                question: 'Is this only for brands getting onto a marketplace?',
                answer:
                    'No. This page is for brands who already sell and cannot scale the listing work — spreadsheets, rejections, colorway splits.',
            },
            {
                id: 'shopify',
                question: 'Shopify only?',
                answer: 'Yes. Shopify is the source catalog. Osello reads it directly. No export, no template.',
            },
            {
                id: 'demo',
                question: 'What happens in the 20 minutes?',
                answer: 'Your catalog on screen. No deck. No sales script.',
            },
            {
                id: 'cost',
                question: 'What does it cost?',
                answer: 'From $499/mo. Flat fee. No commission on what you sell.',
            },
        ] satisfies LandingFaqItem[],
    },
    close: {
        titleLines: ['See what switching would look like for your brand.'],
        subtitleLines: [
            'Review the workflow, your current connections, and the next steps in a 20-minute demo.',
        ],
        ctaLabel: 'Book a 20-min demo',
    },
} as const
